import * as THREE from 'three';
import { createCoreboundRenderer } from './createCoreboundRenderer';
import { createTravelDie } from './portalTravel';
import { getDie } from './dice';
import { observeElementSize } from '../../utils/observeElementSize';
import { createVictoryGalaxy } from './victoryGalaxy';

export const VICTORY_SEQUENCE_DURATION_MS = 8400;

const progress = (time, start, end) =>
  Math.max(0, Math.min(1, (time - start) / (end - start)));
const smooth = (value) => value * value * (3 - 2 * value);

export const getVictoryFrame = (elapsed) => ({
  breath: smooth(progress(elapsed, 0.85, 3.05)),
  crack: smooth(progress(elapsed, 2.95, 3.75)),
  tear: smooth(progress(elapsed, 3.7, 5.85)),
  reveal: smooth(progress(elapsed, 5.2, 6.2)),
  fade: smooth(progress(elapsed, 7.95, 8.4)),
  glass: smooth(progress(elapsed, 2.9, 3.3)),
  caption:
    smooth(progress(elapsed, 0.5, 1.15)) *
    (1 - smooth(progress(elapsed, 2.6, 3.1))),
});

export const createVictoryScene = (host, dieId, faceValues = null) => {
  let renderer;
  try {
    renderer = createCoreboundRenderer({ antialias: true, alpha: false });
  } catch {
    return null;
  }
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;
  host.appendChild(renderer.domElement);
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 40);
  camera.position.set(0, 0, 7);
  const skin = getDie(dieId);
  const galaxy = createVictoryGalaxy({ dieColor: skin.color, cinematic: true });
  scene.add(galaxy.mesh);
  scene.add(new THREE.HemisphereLight('#d6eaff', '#100c26', 2.6));
  const light = new THREE.DirectionalLight('#f3e8ff', 4.5);
  light.position.set(-2, 4, 5);
  scene.add(light);
  const die = createTravelDie(skin.id, faceValues);
  scene.add(die);
  const dieMaterials = new Set();
  die.traverse((object) => {
    if (!object.material) return;
    object.material.transparent = true;
    dieMaterials.add(object.material);
  });
  let available = true;
  const contextLost = (event) => {
    event.preventDefault();
    available = false;
    renderer.domElement.style.visibility = 'hidden';
  };
  renderer.domElement.addEventListener('webglcontextlost', contextLost);
  const stopObserving = observeElementSize(host, () => {
    const width = Math.max(1, host.clientWidth);
    const height = Math.max(1, host.clientHeight);
    renderer.setPixelRatio(
      Math.min(
        window.devicePixelRatio || 1,
        1.5,
        Math.sqrt(1400000 / (width * height))
      )
    );
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    galaxy.resize(camera.aspect);
  });

  return {
    update(elapsed, frame) {
      if (!available) return false;
      galaxy.update(elapsed, frame);
      const approach = smooth(progress(elapsed, 2.65, 3.35));
      const retreat = smooth(progress(elapsed, 3.55, 5.3));
      const movement = Math.max(0, elapsed - 0.85);
      die.position.set(
        0,
        Math.sin(movement * 1.2) * 0.035 * (1 - approach),
        0.6 + approach * 1.8 - retreat * 5
      );
      die.rotation.set(
        -0.25 + approach * 0.15 + retreat * 0.7,
        0.55 + movement * 0.2 - approach * 0.8,
        -0.1 + retreat * 0.5
      );
      const scale = camera.aspect < 0.65 ? 1 : 1.2;
      die.scale.setScalar(scale * (1 + approach * 0.45) * (1 - retreat * 0.7));
      die.visible = retreat < 1;
      dieMaterials.forEach((surface) => {
        surface.opacity = 1 - retreat;
      });
      renderer.render(scene, camera);
      return true;
    },
    dispose() {
      stopObserving();
      renderer.domElement.removeEventListener('webglcontextlost', contextLost);
      const geometries = new Set();
      const materials = new Set();
      const textures = new Set();
      scene.traverse((object) => {
        if (object.geometry) geometries.add(object.geometry);
        if (object.material) {
          materials.add(object.material);
          if (object.material.map) textures.add(object.material.map);
        }
      });
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((surface) => surface.dispose());
      textures.forEach((texture) => texture.dispose());
      renderer.dispose();
      renderer.forceContextLoss?.();
      renderer.domElement.remove();
    },
  };
};
